import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUsersApiService } from '../../../../apis/users/useUsersApiService';
import {
  VisitorUserParams,
  CreateVisitorUserRequest,
  CheckInRequest,
  CheckOutRequest,
} from '../../../../apis/users/users.api.interfaces';

// Query Keys
export const VISITOR_USERS_QUERY_KEY = 'visitor-users';
export const VISITOR_USER_DETAIL_QUERY_KEY = 'visitor-user-detail';
export const VISITOR_USER_DETAILS_QUERY_KEY = 'visitor-user-details';
export const VISITORS_SUMMARY_QUERY_KEY = 'visitors-summary';

export const useVisitorsList = (params: VisitorUserParams, enabled = true) => {
  const { getVisitorUsers } = useUsersApiService();

  return useQuery({
    queryKey: [VISITOR_USERS_QUERY_KEY, params],
    queryFn: () => getVisitorUsers(params),
    enabled,
  });
};

export const useVisitorUserById = (visitorId: string, enabled = true) => {
  const { getVisitorUserById } = useUsersApiService();

  return useQuery({
    queryKey: [VISITOR_USER_DETAIL_QUERY_KEY, visitorId],
    queryFn: () => getVisitorUserById(visitorId),
    enabled: enabled && !!visitorId,
  });
};

export const useVisitorUserDetails = (visitorId: string, enabled = true) => {
  const { getVisitorUserDetails } = useUsersApiService();

  return useQuery({
    queryKey: [VISITOR_USER_DETAILS_QUERY_KEY, visitorId],
    queryFn: () => getVisitorUserDetails(visitorId),
    enabled: enabled && !!visitorId,
  });
};

export const useVisitorsSummary = (enabled = true) => {
  const { getVisitorsSummary } = useUsersApiService();

  return useQuery({
    queryKey: [VISITORS_SUMMARY_QUERY_KEY],
    queryFn: () => getVisitorsSummary(),
    enabled,
  });
};

export const useCreateVisitorUser = () => {
  const queryClient = useQueryClient();
  const { createVisitorUser } = useUsersApiService();

  return useMutation({
    mutationFn: (data: CreateVisitorUserRequest) => createVisitorUser(data),
    onSuccess: () => {
      // Invalidate and refetch visitor users list
      queryClient.invalidateQueries({ queryKey: [VISITOR_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [VISITORS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useUpdateVisitorUser = () => {
  const queryClient = useQueryClient();
  const { updateVisitorUser } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      visitorId,
      data,
    }: {
      visitorId: string;
      data: Partial<CreateVisitorUserRequest>;
    }) => updateVisitorUser(visitorId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch visitor users list
      queryClient.invalidateQueries({ queryKey: [VISITOR_USERS_QUERY_KEY] });
      // Invalidate specific visitor user detail
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAIL_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAILS_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({ queryKey: [VISITORS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useDeleteVisitorUser = () => {
  const queryClient = useQueryClient();
  const { deleteVisitorUser } = useUsersApiService();

  return useMutation({
    mutationFn: (visitorId: string) => deleteVisitorUser(visitorId),
    onSuccess: () => {
      // Invalidate and refetch visitor users list
      queryClient.invalidateQueries({ queryKey: [VISITOR_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [VISITORS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useCheckInVisitor = () => {
  const queryClient = useQueryClient();
  const { checkInVisitor } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      visitorId,
      data,
    }: {
      visitorId: string;
      data?: CheckInRequest;
    }) => checkInVisitor(visitorId, data),
    onSuccess: (_, variables) => {
      // Invalidate visitor users list and detail
      queryClient.invalidateQueries({ queryKey: [VISITOR_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAIL_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAILS_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({ queryKey: [VISITORS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useCheckOutVisitor = () => {
  const queryClient = useQueryClient();
  const { checkOutVisitor } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      visitorId,
      data,
    }: {
      visitorId: string;
      data?: CheckOutRequest;
    }) => checkOutVisitor(visitorId, data),
    onSuccess: (_, variables) => {
      // Invalidate visitor users list and detail
      queryClient.invalidateQueries({ queryKey: [VISITOR_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAIL_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({
        queryKey: [VISITOR_USER_DETAILS_QUERY_KEY, variables.visitorId],
      });
      queryClient.invalidateQueries({ queryKey: [VISITORS_SUMMARY_QUERY_KEY] });
    },
  });
};
